import { useEffect, useMemo } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { FilterControllerButton } from '@insights_module/components/episode_details_screen/FilterControllerButton';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import { riskLevelBackgroundStyle } from '@insights_module/components/episode_details_screen/EpisodeGarmSection';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { CaretIcon } from '@icons/CaretIcon';
import { CaretIconUp } from '@icons/CaretIconUp';
import { CloseIcon } from '@icons/CloseIcon';
import { scrollToSegment } from '@insights_module/utils/episodesUtils';
import { GarmRiskLevelType } from '@insights_module/types/BrandSafety';

const Container = tw.div`max-w-[400px] flex flex-row items-center bg-gray-3 px-2 py-1 gap-2 cursor-pointer rounded-[4px]`;
const CategoryLabel = tw.p`text-[12px] font-normal truncate text-text-and-icon-invert tracking-[0.12px] rounded-[2px] px-[8px] py-[2px]`;
const SegmentCount = tw.p`text-[11px] text-text-and-icon-muted whitespace-nowrap`;
const CloseButton = tw.div`flex justify-center items-center w-[12px] h-[12px] cursor-pointer`;

export const GarmFilterController = () => {
  const { t } = useTranslation();
  const {
    selectedGarmCategory,
    selectedGarmLevel,
    selectedSegment,
    setSelectedSegment,
    setSelectedGarmCategory,
    setSelectedGarmLevel,
  } = useEpisodeDetailsContext();
  const { segmentIdsFilteredByGarm } = useEpisodeSegment();

  const selectedIndex = useMemo(() => {
    if (!segmentIdsFilteredByGarm.length || selectedSegment === null) return -1;

    return segmentIdsFilteredByGarm.indexOf(selectedSegment);
  }, [segmentIdsFilteredByGarm, selectedSegment]);

  const handlePrev = () => {
    if (!selectedIndex) {
      setSelectedSegment(segmentIdsFilteredByGarm[segmentIdsFilteredByGarm.length - 1]);
      return;
    }

    setSelectedSegment(segmentIdsFilteredByGarm[selectedIndex - 1]);
  };

  const handleNext = () => {
    if (selectedIndex === segmentIdsFilteredByGarm.length - 1) {
      setSelectedSegment(segmentIdsFilteredByGarm[0]);
      return;
    }

    setSelectedSegment(segmentIdsFilteredByGarm[selectedIndex + 1]);
  };

  const handleClose = () => {
    setSelectedGarmCategory(null);
    setSelectedGarmLevel(null);
    setSelectedSegment(null);
  };

  const handleGoToSegment = () => {
    if (!selectedSegment) return;

    scrollToSegment(selectedSegment);
  };

  useEffect(() => {
    // If there is no selected segment, select the first segment in the list but only if selected segment is not in the filtered list already
    if (
      segmentIdsFilteredByGarm.length &&
      (selectedSegment === null ||
        (selectedSegment && !segmentIdsFilteredByGarm.includes(selectedSegment)))
    ) {
      setSelectedSegment(segmentIdsFilteredByGarm[0]);
    }
  }, [segmentIdsFilteredByGarm]);

  useEffect(() => {
    if (selectedSegment !== null) {
      scrollToSegment(selectedSegment);
    }
  }, [selectedIndex]);

  if (!selectedGarmCategory) return null;

  return (
    <Container>
      <CategoryLabel
        onClick={handleGoToSegment}
        css={[riskLevelBackgroundStyle[selectedGarmLevel as GarmRiskLevelType]]}>
        {t(`insights.garm_category.${selectedGarmCategory}`)}
      </CategoryLabel>
      <SegmentCount>
        {`${selectedIndex + 1}/${segmentIdsFilteredByGarm.length}`} Segments
      </SegmentCount>
      <FilterControllerButton onClick={handlePrev}>
        <CaretIconUp tw='stroke-text-and-icon w-2' />
      </FilterControllerButton>
      <FilterControllerButton onClick={handleNext}>
        <CaretIcon tw='fill-text-and-icon w-2' />
      </FilterControllerButton>
      <CloseButton onClick={handleClose}>
        <CloseIcon tw='fill-text-and-icon w-[8px] h-[8px]' />
      </CloseButton>
    </Container>
  );
};
